<script>
  // Diagram editor — port of the diagram_room Svelte app, adapted to
  // save into the wiki as a Document (HTML attachment) rather than to
  // a local file via WebUI bindings. The emitted HTML preserves the
  // original format byte-for-byte: same <script id="diagram-data">
  // block, same CSS, same body markup. That keeps the file viewable
  // standalone and readable by the diagram_room app, and lets us
  // parse already-saved diagrams when loading for edit.
  import { onMount } from 'svelte';

  let {
    initial_html = null,
    initial_title = '',
    source_slug = null,
    endpoints = { create: '' },
    csrf = ''
  } = $props();

  // Diagram state
  let elements = $state([]);
  let selectedElement = $state(null);
  let nextId = $state(1);
  let isDragging = $state(false);
  let dragOffset = $state({ x: 0, y: 0 });
  let statusMessage = $state('');
  let saving = $state(false);

  // Canvas dimensions
  let canvasWidth = $state(800);
  let canvasHeight = $state(600);
  let canvasRealWidth = $state('');
  let canvasRealHeight = $state('');
  let currentTitle = $state(initial_title || 'Untitled');

  // Selected element properties
  let selectedColor = $state('#e0e0e0');
  let selectedLabel = $state('');
  let selectedWidth = $state(100);
  let selectedHeight = $state(100);
  let selectedIcon = $state('');
  let selectedRealWidth = $state('');
  let selectedRealHeight = $state('');

  // SVG paths for icons (lifted from the original diagram_room app so
  // the saved HTML stays self-contained — no font/icon CDN required).
  const iconSVGs = {
    home: '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
    user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
    coffee: '<path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" x2="6" y1="2" y2="4"/><line x1="10" x2="10" y1="2" y2="4"/><line x1="14" x2="14" y1="2" y2="4"/>',
    monitor: '<rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/>',
    smartphone: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
    camera: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>',
    mic: '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>',
    door: '<path d="M13 4h3a2 2 0 0 1 2 2v14"/><path d="M2 20h3"/><path d="M13 20h9"/><path d="M10 12v.01"/><path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z"/>'
  };

  function renderIconSVG(icon, size, color) {
    if (!iconSVGs[icon]) return '';
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${iconSVGs[icon]}</svg>`;
  }

  function addElement(type, icon = '') {
    const newElement = {
      id: nextId++,
      type,
      x: 50 + (elements.length * 20),
      y: 50 + (elements.length * 20),
      width: type === 'text' ? 150 : (type === 'icon' ? 30 : 100),
      height: type === 'text' ? 40 : (type === 'icon' ? 30 : 100),
      color: '#e0e0e0',
      label: type === 'text' ? 'Label' : '',
      icon: icon,
      realWidth: '',
      realHeight: '',
      rotation: 0
    };
    elements = [ ...elements, newElement ];
    selectElement(newElement);
  }

  function selectElement(element) {
    selectedElement = element;
    if (element) {
      selectedColor = element.color;
      selectedLabel = element.label;
      selectedWidth = element.width;
      selectedHeight = element.height;
      selectedIcon = element.icon || '';
      selectedRealWidth = element.realWidth || '';
      selectedRealHeight = element.realHeight || '';
    }
  }

  function updateSelectedElement() {
    if (selectedElement) {
      selectedElement.color = selectedColor;
      selectedElement.label = selectedLabel;
      selectedElement.width = selectedWidth;
      selectedElement.height = selectedHeight;
      selectedElement.icon = selectedIcon;
      selectedElement.realWidth = selectedRealWidth;
      selectedElement.realHeight = selectedRealHeight;
      elements = [ ...elements ];
    }
  }

  function deleteSelected() {
    if (selectedElement) {
      elements = elements.filter(e => e.id !== selectedElement.id);
      selectedElement = null;
    }
  }

  function clearCanvas() {
    if (confirm('Clear all elements?')) {
      elements = [];
      selectedElement = null;
      statusMessage = 'Canvas cleared';
    }
  }

  function handleMouseDown(element, event) {
    event.preventDefault();
    selectElement(element);
    isDragging = true;
    dragOffset = { x: event.clientX - element.x, y: event.clientY - element.y };
  }

  function handleMouseMove(event) {
    if (isDragging && selectedElement) {
      selectedElement.x = event.clientX - dragOffset.x;
      selectedElement.y = event.clientY - dragOffset.y;
      elements = [ ...elements ];
    }
  }

  function handleMouseUp() {
    isDragging = false;
  }

  // Compose the standalone HTML file. Same shape as diagram_room's
  // saveDiagram() — anything that parsed the original will parse this.
  function composeHTML() {
    const diagramData = {
      elements,
      canvasWidth,
      canvasHeight,
      canvasRealWidth,
      canvasRealHeight,
      version: '1.0'
    };

    const head = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Room Diagram</title>
  <style>
    body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
    .canvas { position: relative; width: ${canvasWidth}px; height: ${canvasHeight}px; border: 2px solid #333; background: white; }
    .element { position: absolute; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: move; border: 1px solid #666; }
    .rect { border-radius: 4px; }
    .circle { border-radius: 50%; }
    .text { border: none; font-size: 16px; font-weight: bold; }
    .icon { border: none; }
    .dimension-line { position: absolute; pointer-events: none; }
    .dimension-line.horizontal { border-top: 2px solid #e74c3c; border-left: 2px solid #e74c3c; border-right: 2px solid #e74c3c; }
    .dimension-line.horizontal::before, .dimension-line.horizontal::after { content: ''; position: absolute; width: 2px; height: 8px; background: #e74c3c; top: -5px; }
    .dimension-line.horizontal::before { left: 0; }
    .dimension-line.horizontal::after { right: 0; }
    .dimension-line.vertical { border-left: 2px solid #e74c3c; border-top: 2px solid #e74c3c; border-bottom: 2px solid #e74c3c; }
    .dimension-line.vertical::before, .dimension-line.vertical::after { content: ''; position: absolute; height: 2px; width: 8px; background: #e74c3c; left: -5px; }
    .dimension-line.vertical::before { top: 0; }
    .dimension-line.vertical::after { bottom: 0; }
    .dimension-label { position: absolute; background: white; padding: 2px 6px; font-size: 11px; font-weight: bold; color: #e74c3c; border: 1px solid #e74c3c; border-radius: 3px; white-space: nowrap; }
    .dimension-line.horizontal .dimension-label { top: -20px; left: 50%; transform: translateX(-50%); }
    .dimension-line.vertical .dimension-label { left: 10px; top: 50%; transform: translateY(-50%); }
    .dimension-line.canvas-dimension { border-color: #3498db; }
    .dimension-line.canvas-dimension::before, .dimension-line.canvas-dimension::after { background: #3498db; }
    .dimension-line.canvas-dimension .dimension-label { color: #3498db; border-color: #3498db; font-weight: bold; }
  </style>
  <script id="diagram-data" type="application/json">
${JSON.stringify(diagramData, null, 2)}
  <\/script>
</head>`;

    const body = `<body>
  <h1>Room Diagram</h1>
  <div class="canvas">
${elements.map(el => {
  const bg = el.type === 'text' || el.type === 'icon' ? 'transparent' : el.color;
  const content = el.type === 'icon' && el.icon
    ? renderIconSVG(el.icon, Math.min(el.width, el.height) - 10, el.color)
    : el.label;
  let html = `    <div class="element ${el.type}" style="left: ${el.x}px; top: ${el.y}px; width: ${el.width}px; height: ${el.height}px; background: ${bg};">${content}</div>`;
  if (el.realWidth) {
    html += `\n    <div class="dimension-line horizontal" style="left: ${el.x}px; top: ${el.y + el.height + 5}px; width: ${el.width}px;">
      <div class="dimension-label">${el.realWidth}</div>
    </div>`;
  }
  if (el.realHeight) {
    html += `\n    <div class="dimension-line vertical" style="left: ${el.x + el.width + 5}px; top: ${el.y}px; height: ${el.height}px;">
      <div class="dimension-label">${el.realHeight}</div>
    </div>`;
  }
  return html;
}).join('\n')}
${canvasRealWidth ? `    <div class="dimension-line canvas-dimension horizontal" style="left: 0; top: ${canvasHeight + 10}px; width: ${canvasWidth}px;">
      <div class="dimension-label">${canvasRealWidth}</div>
    </div>` : ''}
${canvasRealHeight ? `    <div class="dimension-line canvas-dimension vertical" style="left: ${canvasWidth + 10}px; top: 0; height: ${canvasHeight}px;">
      <div class="dimension-label">${canvasRealHeight}</div>
    </div>` : ''}
  </div>
</body>
</html>`;
    return head + '\n' + body;
  }

  async function saveDiagram() {
    const title = window.prompt('Save diagram as (title):', currentTitle);
    if (title === null) return; // cancelled
    const finalTitle = title.trim() || currentTitle;

    saving = true;
    try {
      const res = await fetch(endpoints.create, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrf
        },
        credentials: 'same-origin',
        body: JSON.stringify({ html: composeHTML(), title: finalTitle })
      });
      let payload = null;
      try { payload = await res.json(); } catch { /* may be empty */ }
      if (!res.ok) {
        const msg = (payload && payload.errors && payload.errors.join('; ')) || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      statusMessage = `Saved "${payload.title}". Redirecting…`;
      window.location.href = payload.url;
    } catch (e) {
      statusMessage = 'Error saving: ' + e.message;
    } finally {
      saving = false;
    }
  }

  // Parse the diagram-data script block out of a saved HTML file and
  // populate state. Tolerant of the format the diagram_room app emits.
  function loadFromHTML(html) {
    const match = html.match(/<script id="diagram-data" type="application\/json">([\s\S]*?)<\/script>/);
    if (!match) {
      statusMessage = 'No diagram data found in source file.';
      return;
    }
    try {
      const data = JSON.parse(match[1]);
      elements = data.elements || [];
      canvasWidth = data.canvasWidth || 800;
      canvasHeight = data.canvasHeight || 600;
      canvasRealWidth = data.canvasRealWidth || '';
      canvasRealHeight = data.canvasRealHeight || '';
      nextId = Math.max(0, ...elements.map(e => e.id || 0)) + 1;
      statusMessage = `Loaded ${elements.length} element${elements.length === 1 ? '' : 's'} from source.`;
    } catch (e) {
      statusMessage = 'Error parsing diagram data: ' + e.message;
    }
  }

  onMount(() => {
    if (initial_html) loadFromHTML(initial_html);
  });
</script>

<svelte:window onmousemove={handleMouseMove} onmouseup={handleMouseUp} />

<main>
  <div class="toolbar">
    <h2>Diagram editor</h2>
    {#if source_slug}
      <p class="note">Editing a copy of an existing diagram. Save will create a new document; the original isn't changed.</p>
    {/if}

    <div class="tool-section">
      <h3>Title</h3>
      <label>
        <input type="text" bind:value={currentTitle} placeholder="Untitled" />
      </label>
    </div>

    <div class="tool-section">
      <h3>Canvas size</h3>
      <label>Width (px): <input type="number" min="400" max="2000" bind:value={canvasWidth} /></label>
      <label>Height (px): <input type="number" min="300" max="1500" bind:value={canvasHeight} /></label>
      <label>Real width: <input type="text" bind:value={canvasRealWidth} placeholder='e.g. "10ft"' /></label>
      <label>Real height: <input type="text" bind:value={canvasRealHeight} placeholder='e.g. "8ft"' /></label>
    </div>

    <div class="tool-section">
      <h3>Add shapes</h3>
      <button onclick={() => addElement('rect')}>Rectangle</button>
      <button onclick={() => addElement('circle')}>Circle</button>
      <button onclick={() => addElement('text')}>Text label</button>
    </div>

    <div class="tool-section">
      <h3>Add icons</h3>
      <button onclick={() => addElement('icon', 'home')}>Home</button>
      <button onclick={() => addElement('icon', 'user')}>User</button>
      <button onclick={() => addElement('icon', 'coffee')}>Coffee</button>
      <button onclick={() => addElement('icon', 'monitor')}>Monitor</button>
      <button onclick={() => addElement('icon', 'smartphone')}>Smartphone</button>
      <button onclick={() => addElement('icon', 'camera')}>Camera</button>
      <button onclick={() => addElement('icon', 'mic')}>Microphone</button>
      <button onclick={() => addElement('icon', 'door')}>Door</button>
    </div>

    <div class="tool-section">
      <h3>File</h3>
      <button onclick={saveDiagram} disabled={saving}>{saving ? 'Saving…' : 'Save as new document'}</button>
      <button onclick={clearCanvas}>Clear canvas</button>
    </div>

    {#if selectedElement}
      <div class="tool-section properties">
        <h3>Properties</h3>
        <label>Label: <input type="text" bind:value={selectedLabel} oninput={updateSelectedElement} /></label>
        {#if selectedElement.type === 'icon'}
          <label>Icon:
            <select bind:value={selectedIcon} oninput={updateSelectedElement}>
              {#each Object.keys(iconSVGs) as name}<option value={name}>{name}</option>{/each}
            </select>
          </label>
        {/if}
        {#if selectedElement.type !== 'text'}
          <label>Color: <input type="color" bind:value={selectedColor} oninput={updateSelectedElement} /></label>
        {/if}
        <label>Width (px): <input type="number" min="20" max="500" bind:value={selectedWidth} oninput={updateSelectedElement} /></label>
        <label>Height (px): <input type="number" min="20" max="500" bind:value={selectedHeight} oninput={updateSelectedElement} /></label>
        <label>Real width: <input type="text" bind:value={selectedRealWidth} oninput={updateSelectedElement} placeholder='e.g. "3ft"' /></label>
        <label>Real height: <input type="text" bind:value={selectedRealHeight} oninput={updateSelectedElement} placeholder='e.g. "2ft"' /></label>
        <button onclick={deleteSelected} class="delete-btn">Delete element</button>
      </div>
    {/if}

    {#if statusMessage}<div class="status">{statusMessage}</div>{/if}
  </div>

  <div class="canvas-container">
    <div class="canvas-header"><h2>{currentTitle}</h2></div>
    <div class="canvas" style="width: {canvasWidth}px; height: {canvasHeight}px;">
      {#each elements as element (element.id)}
        <div
          role="button"
          tabindex="0"
          class="element {element.type}"
          class:selected={selectedElement?.id === element.id}
          style="left: {element.x}px; top: {element.y}px; width: {element.width}px; height: {element.height}px; background: {element.type === 'text' || element.type === 'icon' ? 'transparent' : element.color};"
          onmousedown={(e) => handleMouseDown(element, e)}
          onkeydown={(e) => e.key === 'Enter' && selectElement(element)}
        >
          {#if element.type === 'icon' && element.icon && iconSVGs[element.icon]}
            {@html renderIconSVG(element.icon, Math.min(element.width, element.height) - 10, element.color)}
          {:else}
            {element.label}
          {/if}
        </div>
        {#if element.realWidth}
          <div class="dimension-line horizontal" style="left: {element.x}px; top: {element.y + element.height + 5}px; width: {element.width}px;">
            <div class="dimension-label">{element.realWidth}</div>
          </div>
        {/if}
        {#if element.realHeight}
          <div class="dimension-line vertical" style="left: {element.x + element.width + 5}px; top: {element.y}px; height: {element.height}px;">
            <div class="dimension-label">{element.realHeight}</div>
          </div>
        {/if}
      {/each}
      {#if canvasRealWidth}
        <div class="dimension-line canvas-dimension horizontal" style="left: 0; top: {canvasHeight + 10}px; width: {canvasWidth}px;">
          <div class="dimension-label">{canvasRealWidth}</div>
        </div>
      {/if}
      {#if canvasRealHeight}
        <div class="dimension-line canvas-dimension vertical" style="left: {canvasWidth + 10}px; top: 0; height: {canvasHeight}px;">
          <div class="dimension-label">{canvasRealHeight}</div>
        </div>
      {/if}
    </div>
  </div>
</main>

<style>
  main {
    display: flex;
    min-height: 80vh;
    background: #f5f5f5;
  }
  .toolbar {
    width: 280px;
    background: #2c3e50;
    color: white;
    padding: 20px;
    overflow-y: auto;
    flex-shrink: 0;
  }
  .toolbar h2 { margin-top: 0; font-size: 20px; }
  .toolbar h3 { margin: 0 0 10px 0; font-size: 14px; text-transform: uppercase; color: #95a5a6; }
  .toolbar label { display: block; margin: 10px 0; font-size: 13px; }
  .toolbar input[type="number"],
  .toolbar input[type="text"] {
    width: 100%; padding: 6px; margin-top: 4px;
    border: 1px solid #34495e; border-radius: 3px;
    background: #34495e; color: white;
  }
  .toolbar select {
    width: 100%; padding: 6px; margin-top: 4px;
    border: 1px solid #34495e; border-radius: 3px;
    background: #34495e; color: white;
  }
  .toolbar button {
    width: 100%; padding: 8px; margin: 4px 0;
    background: #3498db; color: white;
    border: none; border-radius: 4px; cursor: pointer;
    font-size: 13px;
  }
  .toolbar button:hover { background: #2980b9; }
  .toolbar button:disabled { opacity: 0.6; cursor: wait; }
  .delete-btn { background: #e74c3c !important; }
  .delete-btn:hover { background: #c0392b !important; }
  .tool-section { margin: 16px 0; padding: 12px 0; border-top: 1px solid #34495e; }
  .tool-section:first-of-type { border-top: none; }
  .note { background: #f39c12; color: #fff; padding: 8px; border-radius: 3px; font-size: 12px; }
  .status { margin-top: 12px; padding: 8px; background: #27ae60; border-radius: 3px; font-size: 12px; }

  .canvas-container { flex: 1; overflow: auto; display: flex; flex-direction: column; }
  .canvas-header { padding: 12px 18px; background: white; border-bottom: 2px solid #bdc3c7; }
  .canvas-header h2 { margin: 0; font-size: 18px; color: #2c3e50; font-weight: 600; }
  .canvas { position: relative; background: white; margin: 20px; border: 2px solid #bdc3c7; flex-shrink: 0; }

  .element {
    position: absolute;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; cursor: move;
    border: 1px solid #666; user-select: none;
  }
  .element.rect { border-radius: 4px; }
  .element.circle { border-radius: 50%; }
  .element.text { border: none; font-size: 16px; font-weight: bold; color: #2c3e50; }
  .element.icon { border: none; }
  .element.selected { outline: 2px solid #3498db; outline-offset: 2px; }

  .dimension-line { position: absolute; pointer-events: none; }
  .dimension-line.horizontal {
    border-top: 2px solid #e74c3c;
    border-left: 2px solid #e74c3c;
    border-right: 2px solid #e74c3c;
  }
  .dimension-line.horizontal::before,
  .dimension-line.horizontal::after {
    content: ''; position: absolute;
    width: 2px; height: 8px; background: #e74c3c; top: -5px;
  }
  .dimension-line.horizontal::before { left: 0; }
  .dimension-line.horizontal::after { right: 0; }
  .dimension-line.vertical {
    border-left: 2px solid #e74c3c;
    border-top: 2px solid #e74c3c;
    border-bottom: 2px solid #e74c3c;
  }
  .dimension-line.vertical::before,
  .dimension-line.vertical::after {
    content: ''; position: absolute;
    height: 2px; width: 8px; background: #e74c3c; left: -5px;
  }
  .dimension-line.vertical::before { top: 0; }
  .dimension-line.vertical::after { bottom: 0; }
  .dimension-label {
    position: absolute; background: white;
    padding: 2px 6px; font-size: 11px; font-weight: bold;
    color: #e74c3c; border: 1px solid #e74c3c; border-radius: 3px; white-space: nowrap;
  }
  .dimension-line.horizontal .dimension-label { top: -20px; left: 50%; transform: translateX(-50%); }
  .dimension-line.vertical .dimension-label { left: 10px; top: 50%; transform: translateY(-50%); }
  .dimension-line.canvas-dimension { border-color: #3498db; }
  .dimension-line.canvas-dimension::before,
  .dimension-line.canvas-dimension::after { background: #3498db; }
  .dimension-line.canvas-dimension .dimension-label {
    color: #3498db; border-color: #3498db; font-weight: bold;
  }
</style>
